import _ from 'lodash'
import { d } from 'core/service/lib/decimal'
import { roundDown } from 'core/service/lib/number-format'
import VALUES from 'core/data-model/constants/values'

import { calculateMinimumDeathCoverage } from 'core/service/benefit-illustration/products/retirement'
import { calculateSurrenderCash, queryCashValueRate } from 'core/service/basic-plan/surrender-cash'

export const getInitialAge = (age) => (age.unit === 'year' ? age.value : 0)

export const getCoveragePeriodValue = (age, coveragePeriod) => {
  switch (coveragePeriod.type) {
    case VALUES.PERIOD_LIMITED_TO_AGE:
      return coveragePeriod.value
    case VALUES.PERIOD_BY_PACKAGE:
    case VALUES.PERIOD_LIMITED_BY_DURATION:
    default:
      return coveragePeriod.value + age
  }
}

export const getAgeRangeWithinCoveragePeriod = (age, coveragePeriod) => {
  const coveragePeriodValue = getCoveragePeriodValue(age, coveragePeriod)
  return _.range(age + 1, coveragePeriodValue + 1)
}

export const calculateAnnualBasicPremium = ({ basicPremium, modelFactorPeriod }) =>
  d(roundDown(d(basicPremium).times(modelFactorPeriod), 2)).toNumber()

export const calculateGuarenteeDividend = ({ sumAssured, rate }) =>
  d(sumAssured)
    .times(rate)
    .div(1000)
    .toNumber()

export const calculateInterestNonGuaranteeDividendDeposit = ({
  previousNonGuranteeDividendDeposit,
  guaranteeDividendOfYear,
  interestRate,
}) => {
  return d(previousNonGuranteeDividendDeposit)
    .times(interestRate)
    .plus(guaranteeDividendOfYear)
    .toNumber()
}

export const calculateInterestGuaranteeDividend = ({ guaranteeDividendOfYear, nonGuaranteeDividendOfYear }) => {
  return d(nonGuaranteeDividendOfYear)
    .minus(guaranteeDividendOfYear)
    .toNumber()
}

export const calculateTotalDividend = ({ cumulativeGuaranteeDividend, interest }) => {
  return d(cumulativeGuaranteeDividend)
    .plus(interest)
    .toNumber()
}

export const calculateTotalBenefit = ({ deathBenefit, previousNonGuranteeDividend }) => {
  return d(deathBenefit)
    .plus(previousNonGuranteeDividend)
    .toNumber()
}

export const getSurrenderCash = ({
  basicPlan,
  gender,
  initialInsuredAge,
  yearEnd,
  cashValueRates,
  basicSumAssured,
}) => {
  const cashValueRate = queryCashValueRate(basicPlan.basicPlanCode, gender, initialInsuredAge, yearEnd, cashValueRates)
  if (
    basicPlan.basicPlanCode === VALUES.LIFE_SUPER_SAVE_14_5_CODE ||
    basicPlan.basicPlanCode === VALUES.LIFE_ULTRA_SAVE_10_3_CODE
  )
    return calculateSurrenderCash(cashValueRate, basicSumAssured, 2)
  else return calculateSurrenderCash(cashValueRate, basicSumAssured)
}

export const calculateDeathBenefit = ({
  basicPlan,
  basicSumAssured,
  cumulativeBasicPremium = 0,
  surrenderCash = 0,
  yearEnd,
}) => {
  const basicPlanCode = _.get(basicPlan, 'basicPlanCode')

  switch (basicPlanCode) {
    case '20P10':
    case '25P15':
    case '20P10S':
    case '25P15S':
      return basicSumAssured + cumulativeBasicPremium
    case 'WL88S':
    case 'WL88J':
    case 'W85P10J':
    case 'W85P10S':
    case 'CIWLM39':
    case 'CIWLM50':
    case 'CIWLM60':
    case 'CIWLS39':
    case 'CIWLS50':
    case 'CIWLS65':
    case 'W99F99H':
    case 'W99F99M':
    case 'W99FU06':
    case 'W99FU12':
    case 'W99U18W':
    case 'W99H18W':
    case 'W99FU18':
    case 'W99FH18':
    case 'W99FU99':
    case 'W99FH99':
    case 'H99F06A':
    case 'H99F12A':
    case 'H99F18A':
    case 'W99F06A':
      const cumulativeBasicPremiumFinal = d(cumulativeBasicPremium).plus(d(cumulativeBasicPremium).times(0.01))
      return Math.max(basicSumAssured, cumulativeBasicPremiumFinal, surrenderCash, cumulativeBasicPremium)
    case 'R01':
    case 'R05':
      const retirementAccumulatedBasicPremium = d(cumulativeBasicPremium).plus(d(cumulativeBasicPremium).times(0.1))
      const doubleBasicSumAssured = d(basicSumAssured).times(VALUES.PERCENT_MINIMUM_DEATH_COVERAGE.surrender)
      return Math.max(doubleBasicSumAssured, retirementAccumulatedBasicPremium, surrenderCash)
    case 'LR05':
      const minDeathCoverage = d(basicSumAssured).times(VALUES.PERCENT_MINIMUM_DEATH_COVERAGE_LR05.surrender)
      return Math.max(minDeathCoverage, cumulativeBasicPremium, surrenderCash)
    case 'R08F':
    case 'R60F':
      const deathCoverage = calculateMinimumDeathCoverage(basicSumAssured, basicPlanCode)
      const minDeathCoverageBumnanReady =
        yearEnd < 5 ? deathCoverage.beforeFiveYearInsurance : deathCoverage.afterFiveYearInsurance
      const bumnanReadyAccumulatedBasicPremium = d(cumulativeBasicPremium).plus(d(cumulativeBasicPremium).times(0.01))
      return Math.max(minDeathCoverageBumnanReady, bumnanReadyAccumulatedBasicPremium, surrenderCash)
    case 'R9506M1':
    case 'R9506M2':
    case 'R9506M3':
    case 'R9506M4':
    case 'R9555M':
    case 'R9560M':
    case 'R9565M':
    case 'R9570M':
      const bumnanSmartAccumulatedBasicPremium =
        yearEnd < 3 ? d(cumulativeBasicPremium) : d(cumulativeBasicPremium).plus(d(cumulativeBasicPremium).times(0.1))
      return Math.max(bumnanSmartAccumulatedBasicPremium, surrenderCash)
    case '25PL_FLEXI': //new logic because cash drop
    case '25PL':
    case 'FWLNP60':
    case 'IGROW':
      return basicSumAssured
    case '12PL':
      return Math.max(basicSumAssured, surrenderCash)
    case 'E20F10':
    case 'E25F15':
    case 'E25F25':
      const cumulativeBasicPremium101 = d(cumulativeBasicPremium).times(1.01)
      const tempBasicSumAssured = yearEnd > 5 ? basicSumAssured * 2 : basicSumAssured
      return Math.max(cumulativeBasicPremium101, tempBasicSumAssured, surrenderCash)
    case 'S14G5T1':
      return Math.max(calculateGioDeathBenefit(basicSumAssured, yearEnd), surrenderCash)
    case 'E10G3A':
      return Math.max(calculateE10G3ADeathBenefit(basicSumAssured, yearEnd), surrenderCash)
    case 'E16G6A':
    case 'E16G6AS':
    case 'E25G5A':
    case 'E25G5AS':
    case 'E15G8A':
    case 'E15G8AS':
      return Math.max(calculateE16G6ADeathBenefit(basicSumAssured, yearEnd), surrenderCash)
    case 'W80F06': {
      const cumulativeBasicPremium101 = d(cumulativeBasicPremium).times(1.01)
      return Math.max(basicSumAssured * 2, cumulativeBasicPremium101, surrenderCash)
    }
    default:
      return Math.max(basicSumAssured, cumulativeBasicPremium)
  }
}

const calculateE16G6ADeathBenefit = (basicSumAssured, yearEnd) => {
  switch (yearEnd) {
    case 1:
      return d(basicSumAssured).times(1.01)
    case 2:
      return d(basicSumAssured).times(2.02)
    case 3:
      return d(basicSumAssured).times(3.15)
    case 4:
      return d(basicSumAssured).times(4.2)
    case 5:
      return d(basicSumAssured).times(5.25)
    default:
      //year 6-16
      return d(basicSumAssured).times(6.3)
  }
}

const calculateE10G3ADeathBenefit = (basicSumAssured, yearEnd) => {
  switch (yearEnd) {
    case 1:
      return d(basicSumAssured).times(1.01)
    case 2:
      return d(basicSumAssured).times(2.02)
    case 10:
      return d(basicSumAssured).times(3.5)
    default:
      //year 3-9
      return d(basicSumAssured).times(3.15)
  }
}

const calculateGioDeathBenefit = (basicSumAssured, yearEnd) => {
  switch (yearEnd) {
    case 1:
      return d(basicSumAssured).times(1.01)
    case 2:
      return d(basicSumAssured).times(2.02)
    case 3:
      return d(basicSumAssured).times(3.15)
    case 4:
      return d(basicSumAssured).times(4.2)
    default:
      //year 5-14
      return d(basicSumAssured).times(5.25)
  }
}

export const calculateMaturity = ({ basicSumAssured, maturityRate }) => {
  return d(basicSumAssured)
    .times(maturityRate)
    .div(1000)
    .toNumber()
}

export const sumPolicyValuesByColumn = (policyValues, key) => {
  return _.map(policyValues, key)
    .reduce((a, b) => a.add(b), d(0))
    .toNumber()
}

/**
 * NPV calculation helper
 */
const npv = (cashFlows, rate) => {
  let sum = 0
  for (let t = 0; t < cashFlows.length; t++) {
    sum += cashFlows[t] / Math.pow(1 + rate, t)
  }
  return sum
}

/**
 * calculateIRR - Internal Rate of Return (Newton-Raphson with bisection fallback)
 * Handles both positive and negative IRR values
 * Fixed to handle negative IRR cases properly
 */
export const calculateIRR = (cashFlows, guess = 0.1) => {
  if (!Array.isArray(cashFlows) || cashFlows.length < 2) {
    throw new Error('cashFlows must be an array with at least 2 values')
  }
  const hasPos = cashFlows.some((v) => v > 0)
  const hasNeg = cashFlows.some((v) => v < 0)
  if (!(hasPos && hasNeg)) {
    throw new Error('cashFlows must contain at least one positive and one negative value')
  }

  const maxIterations = 1000
  const precision = 1e-8

  // Smart initial guess based on NPV at rate=0
  const npv0 = npv(cashFlows, 0)
  let initialGuess = guess

  // If NPV at 0% is negative, IRR is likely negative
  // This is critical for pension/annuity products where you pay upfront
  if (npv0 < 0) {
    initialGuess = -0.05 // Start search in negative territory
  }

  let rate = initialGuess

  // --- Try Newton-Raphson first ---
  for (let i = 0; i < maxIterations; i++) {
    let sum = 0
    let derivative = 0

    for (let t = 0; t < cashFlows.length; t++) {
      const denom = Math.pow(1 + rate, t)
      sum += cashFlows[t] / denom
      derivative -= (t * cashFlows[t]) / Math.pow(1 + rate, t + 1)
    }

    if (Math.abs(sum) < precision) {
      return rate // Found root
    }

    // If derivative is too small, skip Newton and use bisection
    if (Math.abs(derivative) < 1e-14) {
      break
    }

    let newRate = rate - sum / derivative

    // Stricter clamping to prevent explosion
    if (newRate <= -0.999) newRate = -0.999
    if (newRate >= 10) newRate = 10

    // If Newton is diverging wildly (jumping > 100%), stop and use bisection
    if (Math.abs(newRate - rate) > 1) {
      break
    }

    if (Math.abs(newRate - rate) < precision) {
      return newRate
    }

    rate = newRate
  }

  // --- Fallback: Bisection method ---
  // Extended search range with finer granularity for negative rates
  // This is crucial for scenarios where IRR is negative (e.g., pension products)
  let lo = null,
    hi = null
  let flo = null,
    fhi = null

  // Build scan points with denser sampling in negative range
  const scanPoints = []

  // Fine scan in negative range (-10% to 0%) with 0.25% steps
  for (let r = -0.1; r < 0; r += 0.0025) {
    scanPoints.push(r)
  }

  // Regular scan in positive range (0% to 50%) with 0.5% steps
  for (let r = 0; r <= 0.5; r += 0.005) {
    scanPoints.push(r)
  }

  for (const r of scanPoints) {
    const f = npv(cashFlows, r)

    if (lo === null) {
      lo = r
      flo = f
    } else {
      hi = r
      fhi = f

      // Check if sign change occurred
      if (flo * fhi < 0) {
        // Found bracket with sign change - do bisection
        for (let j = 0; j < 500; j++) {
          const mid = 0.5 * (lo + hi)
          const fmid = npv(cashFlows, mid)

          if (Math.abs(fmid) < 1e-10 || Math.abs(hi - lo) < 1e-12) {
            return mid
          }

          if (flo * fmid < 0) {
            hi = mid
            fhi = fmid
          } else {
            lo = mid
            flo = fmid
          }
        }
        return 0.5 * (lo + hi)
      }

      // Move to next bracket
      lo = hi
      flo = fhi
    }
  }

  throw new Error(`IRR not found in range [-0.10, 0.5]. NPV at 0%: ${npv0.toFixed(2)}`)
}

/** Normalize to UTC midnight to avoid timezone effects */
const toUTCDate = (d) => {
  const dt = d instanceof Date ? d : new Date(d)
  if (Number.isNaN(dt.getTime())) throw new Error('Invalid date detected')
  return new Date(Date.UTC(dt.getUTCFullYear(), dt.getUTCMonth(), dt.getUTCDate()))
}

/** Parse D/M/YYYY or DD/MM/YYYY explicitly (else fallback to toUTCDate) */
const parseDMYFlexible = (s) => {
  const str = String(s || '').trim()
  const m = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/)
  if (m) {
    const d = parseInt(m[1], 10)
    const M = parseInt(m[2], 10)
    const y = parseInt(m[3], 10)
    return new Date(Date.UTC(y, M - 1, d))
  }
  return toUTCDate(str)
}

/** XNPV with Actual/365.25 convention (matches Excel) */
const xnpv = (cashFlows, dates, rate) => {
  const d0 = toUTCDate(dates[0])
  const msPerDay = 24 * 60 * 60 * 1000
  let sum = 0
  for (let i = 0; i < cashFlows.length; i++) {
    const di = toUTCDate(dates[i])
    const t = (di - d0) / msPerDay / 365.25
    sum += cashFlows[i] / Math.pow(1 + rate, t)
  }
  return sum
}

/** Accept numbers/strings (e.g., "1,270.00" or "-") and coerce to number */
const normalizeAmount = (val) => {
  if (typeof val === 'number') return val
  if (typeof val === 'string') {
    const s = val.trim()
    if (!s || s === '-') return 0
    const n = Number(s.replace(/,/g, ''))
    if (!Number.isFinite(n)) throw new Error(`Invalid amount: ${val}`)
    return n
  }
  if (val == null) return 0
  const n = Number(val)
  if (!Number.isFinite(n)) throw new Error(`Invalid amount: ${val}`)
  return n
}

/** Overload normalizer for calculateXIRR */
const normalizeXirrArgs = (a, b, c) => {
  if (Array.isArray(a) && Array.isArray(b)) {
    return { cashFlows: a, dates: b, guess: typeof c === 'number' ? c : 0.1 }
  }
  if (a && typeof a === 'object' && !Array.isArray(a) && !(a instanceof Map)) {
    const entries = Object.entries(a)
      .filter(([, v]) => v != null && String(v).trim() !== '' && String(v).trim() !== '-')
      .map(([k, v]) => [parseDMYFlexible(k), normalizeAmount(v)])
      .sort((e1, e2) => e1[0] - e2[0])
    if (entries.length < 2) throw new Error('At least two non-empty entries are required')
    return {
      cashFlows: entries.map((e) => e[1]),
      dates: entries.map((e) => e[0]),
      guess: typeof b === 'number' ? b : 0.1,
    }
  }
  if (a instanceof Map) {
    const entries = Array.from(a.entries())
      .filter(([, v]) => v != null && String(v).trim() !== '' && String(v).trim() !== '-')
      .map(([k, v]) => [parseDMYFlexible(k), normalizeAmount(v)])
      .sort((e1, e2) => e1[0] - e2[0])
    if (entries.length < 2) throw new Error('At least two non-empty entries are required')
    return {
      cashFlows: entries.map((e) => e[1]),
      dates: entries.map((e) => e[0]),
      guess: typeof b === 'number' ? b : 0.1,
    }
  }
  if (Array.isArray(a) && a.length && typeof a[0] === 'object') {
    const entries = a
      .filter(
        (row) =>
          row &&
          row.date != null &&
          row.amount != null &&
          String(row.amount).trim() !== '-' &&
          String(row.amount).trim() !== ''
      )
      .map((row) => [parseDMYFlexible(row.date), normalizeAmount(row.amount)])
      .sort((e1, e2) => e1[0] - e2[0])
    if (entries.length < 2) throw new Error('At least two non-empty entries are required')
    return {
      cashFlows: entries.map((e) => e[1]),
      dates: entries.map((e) => e[0]),
      guess: typeof b === 'number' ? b : 0.1,
    }
  }
  throw new Error('Unsupported arguments for calculateXIRR')
}

/**
 * Robust XIRR using hybrid Newton + Secant + Bisection fallback (Actual/365.25)
 */
export const calculateXIRR = (a, b, c) => {
  const { cashFlows, dates, guess } = normalizeXirrArgs(a, b, c)

  const hasPos = cashFlows.some((v) => v > 0)
  const hasNeg = cashFlows.some((v) => v < 0)
  if (!(hasPos && hasNeg)) {
    throw new Error('cashFlows must include at least one positive and one negative value')
  }
  if (dates.length !== cashFlows.length) throw new Error('cashFlows and dates lengths must match')
  const timeSet = new Set(dates.map((d) => toUTCDate(d).getTime()))
  if (timeSet.size < 2) throw new Error('dates must contain at least two distinct values')

  // --- Core XNPV and derivative (Actual/365.25) ---
  const npvTol = 1e-15
  const stepTol = 1e-12
  const minRate = -0.999999
  const maxRate = 10

  function F(rate) {
    return xnpv(cashFlows, dates, rate)
  }

  function Fp(rate) {
    const d0 = toUTCDate(dates[0])
    const msPerDay = 24 * 60 * 60 * 1000
    let fp = 0
    for (let k = 0; k < cashFlows.length; k++) {
      const dk = toUTCDate(dates[k])
      const t = (dk - d0) / msPerDay / 365.25
      fp -= (t * cashFlows[k]) / Math.pow(1 + rate, t + 1)
    }
    return fp
  }

  function clamp(r) {
    if (!Number.isFinite(r) || isNaN(r)) return null
    if (r <= minRate) return minRate + 1e-9
    if (r > maxRate) return maxRate
    return r
  }

  // --- 1) Dense bracketing scan (very fine near 0%..10%) ---
  function buildScanGrid() {
    const grid = []

    for (let r = -0.9; r <= -0.01 + 1e-12; r += 0.01) grid.push(r)
    for (let r = 0.00001; r <= 0.1 + 1e-12; r += 0.00025) grid.push(r)
    for (let r = 0.1; r <= 0.5 + 1e-12; r += 0.005) grid.push(r)
    for (let r = 0.5; r <= 1.0 + 1e-12; r += 0.01) grid.push(r)
    for (let r = 1.0; r <= 10.0 + 1e-12; r += 0.05) grid.push(r)

    const uniq = Array.from(new Set(grid.map((x) => +x.toFixed(6))))
      .map(clamp)
      .filter((r) => r !== null)
    uniq.sort((a, b) => a - b)
    return uniq
  }

  function bracketAll() {
    const x = buildScanGrid()
    const brackets = []
    let prevR = null,
      prevF = null

    for (const r of x) {
      const fr = F(r)
      if (prevR !== null) {
        if (Math.abs(fr) < npvTol) {
          brackets.push([r - 1e-6, r + 1e-6])
        } else if (prevF * fr < 0) {
          brackets.push([prevR, r])
        }
      }
      prevR = r
      prevF = fr
    }
    return brackets
  }

  function bisect(lo, hi) {
    lo = clamp(lo)
    hi = clamp(hi)
    if (lo === null || hi === null) return null
    let flo = F(lo),
      fhi = F(hi)
    if (isNaN(flo) || isNaN(fhi)) return null
    if (Math.abs(flo) < 1e-6) return lo
    if (Math.abs(fhi) < 1e-6) return hi
    if (flo * fhi > 0) return null

    for (let i = 0; i < 500; i++) {
      const mid = 0.5 * (lo + hi)
      const fmid = F(mid)
      if (Math.abs(fmid) < npvTol || Math.abs(hi - lo) < stepTol) return mid
      if (flo * fmid < 0) {
        hi = mid
        fhi = fmid
      } else {
        lo = mid
        flo = fmid
      }
    }
    return 0.5 * (lo + hi)
  }

  // --- 2) Collect all root candidates via bracketing+bisection ---
  const rawBrackets = bracketAll()
  const candidates = []
  for (const [lo, hi] of rawBrackets) {
    const r = bisect(lo, hi)
    if (r === null || !Number.isFinite(r)) continue
    const already = candidates.some((x) => Math.abs(x.r - r) < 1e-8)
    if (!already) candidates.push({ r, f: Math.abs(F(r)) })
  }

  // --- 3) If candidates exist, choose the smallest positive root ---
  if (candidates.length) {
    const positives = candidates.filter((x) => x.r > 0)
    if (positives.length) {
      positives.sort((a, b) => a.r - b.r || a.f - b.f)
      return positives[0].r
    }
    candidates.sort((a, b) => a.f - b.f)
    return candidates[0].r
  }

  // --- 4) Fallback: hybrid Newton/Secant/Bisection from multi-start seeds ---
  function solveFrom(start) {
    let rate = clamp(start)
    if (rate === null) return null

    const maxIter = 1000
    let prevRate = null,
      prevF = null

    const scan = [
      -0.9,
      -0.5,
      -0.2,
      -0.1,
      -0.05,
      0,
      0.001,
      0.005,
      0.01,
      0.015,
      0.02,
      0.03,
      0.05,
      0.1,
      0.2,
      0.3,
      0.5,
      1,
      2,
      3,
      5,
      8,
      10,
    ]
    let bracket = null
    let pR = null,
      pF = null
    for (const r of scan) {
      const fr = F(r)
      if (pR !== null && pF * fr < 0) {
        bracket = { lo: pR, hi: r }
        break
      }
      pR = r
      pF = fr
    }

    for (let i = 0; i < maxIter; i++) {
      const f = F(rate),
        fp = Fp(rate)
      if (Math.abs(f) < npvTol) return rate
      let next = rate

      if (Math.abs(fp) > 1e-12) {
        next = rate - f / fp
      } else if (prevF !== null && rate !== prevRate && f !== prevF) {
        next = rate - (f * (rate - prevRate)) / (f - prevF)
      } else if (bracket) {
        next = 0.5 * (bracket.lo + bracket.hi)
      } else {
        next = rate + (rate >= 0 ? 1e-4 : -1e-4)
      }

      next = clamp(next)
      if (next === null) return null
      if (Math.abs(next - rate) > 1) next = rate + Math.sign(next - rate) * 1

      if (bracket) {
        const fnext = F(next)
        const flo = F(bracket.lo),
          fhi = F(bracket.hi)
        if (flo * fnext < 0) bracket.hi = next
        else if (fhi * fnext < 0) bracket.lo = next
      }

      if (Math.abs(next - rate) < stepTol) return next
      prevRate = rate
      prevF = f
      rate = next
    }

    if (bracket) return bisect(bracket.lo, bracket.hi)
    return null
  }

  const defaultSeeds = [0.1, 0.05, 0.02, 0.03, 0.015, 0.01, 0.2, 0.3, 0.5, 0.001, -0.05]
  const seeds =
    typeof guess === 'number' && Number.isFinite(guess)
      ? [guess, ...defaultSeeds.filter((s) => s !== guess)]
      : defaultSeeds

  const fallbacks = []
  for (const s of seeds) {
    const r = solveFrom(s)
    if (r === null || !Number.isFinite(r)) continue
    if (fallbacks.every((x) => Math.abs(x.r - r) > 1e-8)) {
      fallbacks.push({ r, f: Math.abs(F(r)) })
    }
  }
  if (!fallbacks.length) throw new Error('XIRR calculation did not converge')

  const positives = fallbacks.filter((x) => x.r > 0)
  if (positives.length) {
    positives.sort((a, b) => a.r - b.r || a.f - b.f)
    return positives[0].r
  }
  fallbacks.sort((a, b) => a.f - b.f)
  return fallbacks[0].r
}
